import { Injectable, ComponentRef } from "@angular/core";
import { MatDialog } from "@angular/material";

import { GenericTable } from "../../genericTable";
import { tableEditIcon, tableDeleteIcon, tableDownloadIcon } from "../../util";
import { GatewayView } from "./gateway-view";
import {
	GatewaysService,
	GatewaysUiService
} from ".";
import { TranslateService } from "../../common/translate";
import { SnackbarService } from "../../common/snackbar/snackbar.service";
import { SpinnerOverlayService } from "../../common/spinner/spinner-overlay.service";
import { SpinnerOverlayRef } from "../../common/spinner/spinner-overlay.ref";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";
import { GatewayTableHostsComponent } from "./gateway-table-hosts/gateway-table-hosts.component";
import { GatewayTablePortsComponent } from "./gateway-table-ports/gateway-table-ports.component";
import { GatewayTableDnsComponent } from "./gateway-table-dns/gateway-table-dns.component";
import { GatewayEditWizardService } from "./gateway-edit-wizard/gateway-edit-wizard.service";
import { NavigationService } from "../../common/navigation/navigation.service";

@Injectable()
export abstract class GatewaysTableBaseComponent {

	constructor(
		public service: GatewaysService,
		public translateService: TranslateService,
		public snackbarService: SnackbarService,
		public overlay: SpinnerOverlayService,
		public dialog: MatDialog,
		public uiService: GatewaysUiService,
		public gatewayEditWizardService: GatewayEditWizardService,
		private navigationService: NavigationService
	) { }

	public deleteGateway(row: GatewayView) {
		try {
			const message: string = this.translateService.translate("gateway.delete.dialog.message", { label: row.label });

			const dialogRef = this.dialog.open(ConfirmationDialogComponent, {});
			dialogRef.componentInstance.message = message;
			dialogRef.componentInstance.title = "gateway.delete.dialog.title";
			dialogRef.componentInstance.buttonAction = "sdp.label.delete";
			dialogRef.componentInstance.buttonCancel = "sdp.label.cancel";
			dialogRef.afterClosed().subscribe(async (result: boolean) => {
				if (result) {
					let ref: SpinnerOverlayRef | undefined = undefined;
					try {
						ref = this.overlay.open();
						await this.service.deleteGateway(row.uuid);
						this.snackbarService.open(this.translateService.translate("sdp.gateway.delete.success", { label: row.label }));
					} catch (error) {
						this.snackbarService.showErrorBar(error, { label: row.label });
					} finally {
						if (!!ref) {
							ref.close();
						}
					}
				}
			});
		} catch (e) {
			this.snackbarService.showErrorBar(e, { label: row.label });
		}
	}

	public editGateway(row: GatewayView) {
		try {
			this.gatewayEditWizardService.createDialog(row, false);
		} catch (e) {
			this.snackbarService.showErrorBar(e, { label: row.label });
		}
	}

	// shared columns for pending and in/active gateways
	public getBaseGatewaySchema(): GenericTable.ColumnSchema[] {
		return [
			{
				minWidth: "20%",
				type: "string",
				headerSchema: {
					type: "basic",
					sortable: true,
					headerText: "sdp.label.name"
				},
				bodySchema: {
					type: "string",
					boundField: "label"
				}
			}, {
				minWidth: "10%",
				type: "customComponent",
				headerSchema: {
					type: "basic",
					sortable: true,
					headerText: "sdp.label.host"
				},
				bodySchema: {
					type: "customComponent",
					customComponent: GatewayTableHostsComponent,
					callback: (component: ComponentRef<GatewayTableHostsComponent>, data: GatewayView) => {
						component.instance.gateway = data;
					}
				}
			}, {
				minWidth: "10%",
				type: "customComponent",
				headerSchema: {
					type: "basic",
					sortable: true,
					headerText: "sdp.label.port"
				},
				bodySchema: {
					type: "customComponent",
					customComponent: GatewayTablePortsComponent,
					callback: (component: ComponentRef<GatewayTablePortsComponent>, data: GatewayView) => {
						component.instance.gateway = data;
					}
				}
			}, {
				minWidth: "12%",
				type: "customComponent",
				headerSchema: {
					type: "basic",
					sortable: false,
					headerText: "privategateway.dns.label"
				},
				bodySchema: {
					type: "customComponent",
					customComponent: GatewayTableDnsComponent,
					callback: (component: ComponentRef<GatewayTableDnsComponent>, data: GatewayView) => {
						component.instance.gateway = data;
					}
				}
			}, {
				minWidth: "8%",
				type: "button",
				headerSchema: {
					type: "none"
				},
				bodySchema: {
					type: "button",
					buttons: [
						{
							type: "icon",
							icon: tableDownloadIcon,
							tooltip: "gateway.version.download",
							onClick: () => {
								this.navigationService.navigate([`administration/gateways/download`]);
							},
							showButton: (row: GatewayView) => {
								if (row.state === "active" || row.state === "inactive") {
									// Only show this button if the gateway has been accepted, so its one of active or inactive
									return !!row.statuses.find((s) => {
										// Return true if there is a version type status on the gateway
										return s.type === "version";
									});
								} else {
									return false;
								}
							}
						},
						{
							type: "icon",
							icon: tableEditIcon,
							onClick: (row: GatewayView) => {
								this.editGateway(row);
							}
						}, {
							type: "icon",
							icon: tableDeleteIcon,
							tooltip: "gateway.delete.icon.tooltip",
							onClick: (row: GatewayView) => {
								this.deleteGateway(row);
							},
							showButton: (row: GatewayView) => {
								return row.state !== "active";
							}
						}
					]
				}
			}
		];
	}

}
